import React, { useState } from "react";
import { Form, Icon } from "semantic-ui-react";
import { useFormik } from "formik";
import { Auth } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { inititalValues, validationSchema } from "./LoginForm.form";
import { TwoFactorCodeForm } from "../TwoFactorCodeForm/TwoFactorCodeForm";
import "./LoginForm.scss";
import { useLanguage } from "../../../../contexts";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const authController = new Auth();

export function LoginForm({ onRegisterClick, onRecoverClick }) {
  const { login, ssoError, ssoChallenge } = useAuth();
  // Si se llegó desde la otra app y el traspaso falló, el motivo se muestra
  // acá; si allá no se validó 2FA y acá es obligatorio, arranca pidiendo el código.
  const [error, setError] = useState(ssoError || "");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [challengeToken, setChallengeToken] = useState(ssoChallenge || null);
  const [keepSession, setKeepSession] = useState(true);
  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setPasswordVisible(!passwordVisible);
  };

  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const formik = useFormik({
    initialValues: inititalValues(),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const response = await authController.login(formValue);
        if (response.requires2FA) {
          setChallengeToken(response.challengeToken);
          return;
        }
        authController.setAccessToken(response.access, keepSession);
        authController.setRefreshToken(response.refresh, keepSession);
        toast.success(t("login_success"));
        setTimeout(() => login(response.access), 800);
      } catch (error) {
        setError(error.msg || t("server_connection_error"));
      }
    },
  });

  const handleVerified = (access, refresh) => {
    authController.setAccessToken(access, keepSession);
    authController.setRefreshToken(refresh, keepSession);
    toast.success(t("login_success"));
    setTimeout(() => login(access), 800);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !formik.isSubmitting) {
      e.preventDefault();
      formik.handleSubmit();
    }
  };

  return (
    <div className="login-modal-form">
      {challengeToken ? (
        <TwoFactorCodeForm
          challengeToken={challengeToken}
          onVerified={handleVerified}
          onCancel={() => setChallengeToken(null)}
        />
      ) : (
        <Form onSubmit={formik.handleSubmit} className="login-form">
          <Form.Field error={!!formik.errors.email}>
            <label>{t("login_email_label")}</label>
            <input
              name="email"
              type="email"
              autoComplete="username"
              placeholder={t("login_email_placeholder")}
              onChange={formik.handleChange}
              value={formik.values.email}
              onKeyDown={handleKeyPress}
            />
            {formik.errors.email && (
              <div className="ui pointing above prompt label">
                {formik.errors.email}
              </div>
            )}
          </Form.Field>

          <Form.Field error={!!formik.errors.password}>
            <label>{t("login_password_label")}</label>
            <div className="password-input-wrapper">
              <input
                name="password"
                type={passwordVisible ? "text" : "password"}
                autoComplete="current-password"
                placeholder="••••••••"
                onChange={formik.handleChange}
                value={formik.values.password}
                onKeyDown={handleKeyPress}
              />
              <button
                type="button"
                className="password-input-wrapper__eye"
                onClick={togglePasswordVisibility}
              >
                <Icon name={passwordVisible ? "eye slash" : "eye"} />
              </button>
            </div>
            {formik.errors.password && (
              <div className="ui pointing above prompt label">
                {formik.errors.password}
              </div>
            )}
          </Form.Field>

          <div className="login-form__row">
            <label className="login-form__checkbox">
              <input
                type="checkbox"
                checked={keepSession}
                onChange={(e) => setKeepSession(e.target.checked)}
              />
              <span className="login-form__checkbox-box" />
              {t("login_keep_session")}
            </label>
            <a className="recover-password" onClick={onRecoverClick}>
              {t("i_forgot_my_password")}
            </a>
          </div>

          <Form.Button
            type="submit"
            primary
            className="login-form__submit"
            loading={formik.isSubmitting}
          >
            {t("sign_in")}
          </Form.Button>

          <div className="login-form__register">
            {t("login_no_account_question")}{" "}
            <a onClick={onRegisterClick}>{t("register")}</a>
          </div>

          {error && <p className="login-error">{error}</p>}
        </Form>
      )}
    </div>
  );
}
